<template>
  <div class="oai-StructureStep">
    <OnboardingAiStepHeader
      :title-key-accent="$t('onboarding.ai.structure.title_accent')"
      :wizard-step="wizardStep"
      title-key-name="structure"
      title-key-path="onboarding.ai.structure.title_prefix"
    />

    <div v-if="insightKey" class="oai-StructureStep_Insight">
      <div class="oai-StructureStep_LogoWrapper">
        <AppLogo height="36" type="short" width="36" />
      </div>
      <p class="oai-StructureStep_InsightText">
        <i18n-t :keypath="insightKey" scope="global">
          <template #option>
            <span class="oai-StructureStep_InsightOption">{{ recommendedOptionTitle }}</span>
          </template>
        </i18n-t>
      </p>
    </div>

    <div class="oai-StructureStep_Options">
      <div
        v-for="option in structureOptions"
        :key="option.id"
        class="oai-StructureStep_OptionsWrapper"
      >
        <OnboardingAiBadge v-if="option.recommended" class="oai-StructureStep_OptionBadge">
          {{ $t('onboarding.ai.structure.recommended_badge') }}
        </OnboardingAiBadge>
        <div :class="getOptionClasses(option)" class="oai-StructureStep_OptionShell">
          <div class="oai-StructureStep_OptionShell_Inner">
            <OnboardingAiStructureOptionCard
              v-model="selectedStructureId"
              :name="STRUCTURE_RADIO_GROUP_NAME"
              :option="option"
              :value="option.id"
            />
          </div>
        </div>
      </div>
    </div>

    <OnboardingAiStepActions :disable="!canProceed" @next="onNext" />
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  DEFAULT_STRUCTURE_OPTION_ID,
  ONBOARDING_AI_STRUCTURE_OPTIONS,
  useOnboardingAiTracking
} from '@/composables/onboarding-ai-step'
import { EVENT_NAMES } from '@/tracking/amplitude-helpers'

import AppLogo from '@/components/AppLogo'
import OnboardingAiBadge from '@/components/ui/onboarding-ai/OnboardingAiBadge'
import OnboardingAiStepActions from '@/components/ui/onboarding-ai/OnboardingAiStepActions'
import OnboardingAiStepHeader from '@/components/ui/onboarding-ai/OnboardingAiStepHeader'
import OnboardingAiStructureOptionCard from '@/components/ui/onboarding-ai/OnboardingAiStructureOptionCard'

defineOptions({
  name: 'OnboardingAiOkrStructureStep'
})

const { logEvent } = useOnboardingAiTracking()

const STRUCTURE_RADIO_GROUP_NAME = 'onboarding-ai-structure'

const props = defineProps({
  wizardStep: {
    type: Number,
    required: true
  },

  modelValue: {
    type: String,
    default: ''
  },

  recommendedStructureId: {
    type: String,
    default: null
  }
})

const emit = defineEmits({
  'update:modelValue': null,
  next: null
})

const { t } = useI18n()

const insightKey = computed(() => {
  return ONBOARDING_AI_STRUCTURE_OPTIONS.find(e => e.id === props.recommendedStructureId)?.optionKey
})

const recommendedOptionTitle = computed(() => {
  const option = ONBOARDING_AI_STRUCTURE_OPTIONS.find(
    ({ id }) => id === props.recommendedStructureId
  )

  return (
    option?.title ??
    ONBOARDING_AI_STRUCTURE_OPTIONS.find(e => e.id === DEFAULT_STRUCTURE_OPTION_ID)?.title
  )
})

const structureOptions = computed(() =>
  ONBOARDING_AI_STRUCTURE_OPTIONS.map(option => ({
    ...option,
    recommended: props.recommendedStructureId === option.id,
    employees: t(option.employeesKey),
    description: t(option.descriptionKey)
  }))
)

const selectedStructureId = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value)
})

const canProceed = computed(() => Boolean(selectedStructureId.value))

const getOptionClasses = option => {
  const showBorder = selectedStructureId.value === option.id || option.recommended
  return {
    'oai-StructureStep_OptionShell-animated': showBorder,
    'oai-StructureStep_OptionShell-no-border': !showBorder,
    'oai-StructureStep_OptionShell-secondary':
      option.recommended && selectedStructureId.value !== option.id
  }
}

const onNext = () => {
  emit('next', {
    structure_layers: selectedStructureId.value
  })
}

onMounted(() => {
  logEvent(EVENT_NAMES.ONBOARDING_OKR_STRUCTURE_VIEWED)
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/onboarding-ai-step';
@import '~@/assets/styles/gradient-border';

.oai-StructureStep {
  @extend %oai-wizard-step-shell;
}

.oai-StructureStep_Insight {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.oai-StructureStep_InsightText {
  margin: 0;
  font-size: $fs-12;
  line-height: 18px;
  font-weight: fw('regular');
  color: $oai-text-action;
}

.oai-StructureStep_InsightOption {
  font-weight: fw('semi-bold');
}

.oai-StructureStep_Options {
  display: grid;
  gap: 16px;
  width: 100%;
}

.oai-StructureStep_OptionsWrapper {
  position: relative;
  overflow: visible;
}

.oai-StructureStep_OptionShell {
  @extend %oai-gradient-border;

  &-animated {
    @extend %oai-gradient-border--animated;
  }

  &-no-border {
    @extend %oai-gradient-border--no-border;

    .oai-StructureStep_OptionShell_Inner {
      border-radius: var(--ob-oai-gradient-border-radius);
    }
  }

  &-secondary {
    --ob-oai-gradient-border-alpha: 40%;
  }
}

.oai-StructureStep_OptionShell_Inner {
  @extend %oai-gradient-border-inner;
}

.oai-StructureStep_OptionBadge {
  position: absolute;
  z-index: 2;
  top: 0;
  right: 24px;
  transform: translateY(-50%);
}

.oai-StructureStep_LogoWrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $white;
  width: 36px;
  height: 36px;
  border-radius: $border-radius-sm;
  flex-shrink: 0;
}
</style>
